/* eslint-disable jsx-a11y/no-autofocus */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef, useCallback } from "react";
import { Box, Alert, Link, DialogContent } from "@mui/material";
import Button from "@mui/material/Button";
import { useNavigate, useLocation } from "react-router-dom";
import { USERSIGNUPMESSAGES } from "../../constants/messages";
import Cookies from "js-cookie";
import {
  VERIFICATIONTYPE,
  SIGNINRESPONSES,
  USERINFO,
  SIGNUPMODE,
  COOKIESNAME,
  SYSTEMSETTINGS,
  GLOBALSETTINGS,
  AUDITRACEACTIONS,
  LOCALSTORAGENAME,
} from "../../data/constants";
import Typography from "@mui/material/Typography";
import VerifiedIcon from "@mui/icons-material/Verified";
import useApi from "../../hooks/useApi";
import { USERSIGNINMESSAGES } from "../../constants/messages";
import {
  verify2FACodeByUserId,
  verifyUserSignIn,
  updateLoginTimeStamp,
  increaseFailedAttemptsCount,
  getEmployeeProfileImage,
} from "../../services/userService";
import {
  VerifyTwoFactorUserIdDto,
  VerifySignInDto,
  LoginTimeStampDto,
  IncreaseFailedAttemptsCountDto,
  TwoFactorResultDto,
} from "../../types/User";
import { IconButton, Dialog, DialogTitle, DialogActions } from "@mui/material";
import BackdropProgressBar from "../../components/ui/BackdropProgressBar";
import CloseIcon from "@mui/icons-material/Close";
import { useSettingValue } from "../../services/useSettingValue";
import SetUpTwoFactorAuth, { SetUpTwoFactorAuthRef } from "./SetUpTwoFactorAuth";
import { useUser } from "../../context/UserContext";
import { setToken, shouldUseEmployeePhotoFallback } from "../../utils/commonHelper";
import { useSearchParams } from "react-router-dom";
import { logAuditTrace } from "../../services/auditNavigationTraceService";
import { getCurrentESTTime } from "../../utils/dateHelper";
import { getUserModuleRolePermissions } from "../../services/portalUserService";
import { UserModuleRolePermission } from "../../types/UserModuleRolePermission";
import OtpInput from "../CommonComponent/CustomOTP";

interface SigninWithOTPProps {
  onOtpVerified: (otp: string, isOtpMatched: boolean) => void;
  // isAdminLocked is only known when the result came from verify-sign-in; omit it and the parent
  // falls back to the neutral lock message rather than guessing the lock reason.
  onCheckIsValidUser: (response: string, isAdminLocked?: boolean) => void;
  onParentAction: () => Promise<boolean>;
  userInput: string;
  otpSendTo: string;
  headerText: string;
  rememberForDevice: boolean;
  deviceId: string;
  password: string;
  onIsSessionLocked: () => void;
  authModeName: string;
  onCallParentToRoleStatus: (response: string) => void;
}

const SignInWithOTP: React.FC<SigninWithOTPProps> = ({
  onCheckIsValidUser,
  onParentAction,
  userInput,
  otpSendTo,
  headerText,
  rememberForDevice,
  deviceId,
  onIsSessionLocked,
  authModeName,
  onCallParentToRoleStatus,
}) => {
  const [otpInput, setOtpInput] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const [validatedUserId, setValidatedUserId] = useState(0);
  const [validatedUserName, setValidatedUserName] = useState("");
  const [validatedFistName, setValidatedFirstName] = useState("");
  const [validatedLastName, setValidatedLastName] = useState("");
  // Bug 79975: null (never set) and "" (explicitly removed) have to stay distinguishable here so
  // the HR employee photo is only used as a fallback in the former case.
  const [validatedProfileImage, setValidatedProfileImage] = useState<string | null>(null);
  const [userEmail, setUserEmail] = useState("");
  const [userPhone, setUserphone] = useState("");
  const [isSessionLocked, setIsSessionLocked] = React.useState(false);
  const [isAccountLocked, setIsAccountLocked] = React.useState(false);
  const [openConfirmation2FA, setOpenConfirmation2FA] = React.useState(false);
  const [confirmationDialogMsg, setConfirmationDialogMsg] = React.useState("");
  const { setUserId, setUserName, setFirstName, setLastName, setProfileImage } = useUser();
  const [searchParams] = useSearchParams();
  const otpRef = useRef(null);
  const [otpStatus, setOtpStatus] = useState({
    isVisibleSendOtp: true,
    isOtpSent: false,
    timer: 30,
    isResendDisabled: true,
    otpErrorMessage: "",
    isVerified: false,
    otpSentTo: "",
  });
  const [openOTPDialog, setOpenOTPDialog] = React.useState(false);
  const updateOtpStatusState = useCallback((newState: Partial<typeof otpStatus>) => {
    setOtpStatus((prevState) => ({
      ...prevState,
      ...newState,
    }));
  }, []);
  const { loading, executeApi } = useApi();
  const firstTextFieldRef = useRef<HTMLInputElement>(null);
  const otpFieldLength =
    Number(useSettingValue(SYSTEMSETTINGS.OTP_FIELD_LENGTH)) ||
    GLOBALSETTINGS.DEFAULT_OTP_FIELD_LENGTH;
  const setUpTwoFactorAuthRef = useRef<SetUpTwoFactorAuthRef>(null);
  useEffect(() => {
    let countdown: ReturnType<typeof setTimeout>;
    if (otpStatus.timer > 0) {
      countdown = setTimeout(() => updateOtpStatusState({ timer: otpStatus.timer - 1 }), 1000);
    } else {
      updateOtpStatusState({ isResendDisabled: false });
    }
    return () => clearTimeout(countdown);
  }, [otpStatus.timer, 1]);
  const handleVerifyPasswordAndSendOtp = async () => {
    if (!(await onParentAction())) return;
    if (!(await onVerifyUserInput())) return;
    clearOtp();
    setOpenOTPDialog(true);
    setTimeout(() => {
      focusFirstTextField();
    }, 100);
  };
  const handleSendOtp = async () => {
    if (!(await onVerifyUserInput())) return;
    clearOtp();
    setOpenOTPDialog(true);
    setTimeout(() => {
      focusFirstTextField();
    }, 100);
  };
  const clearOtp = () => {
    setOtpInput("");
    otpRef.current?.clear();
    focusFirstTextField();
    updateOtpStatusState({
      otpErrorMessage: "",
    });
  };
  const onVerifyUserInput = async () => {
    setIsAccountLocked(false);
    setIsSessionLocked(false);
    if (!userInput || userInput.trim() === "") {
      onCheckIsValidUser("");
      return false;
    }
    const sessionValue = localStorage.getItem(USERINFO.SESSIONVALUE);
    const userSessionData: VerifySignInDto = {
      userInput: userInput,
      sessionValue: sessionValue,
      verificationType: VERIFICATIONTYPE.TWOFA,
    };
    const userDetails = await executeApi<VerifySignInDto, VerifySignInDto>(
      verifyUserSignIn,
      userSessionData,
    );
    if (userDetails && userDetails.result === SIGNINRESPONSES.VALID_USER) {
      setUserEmail(userDetails.cachedEmailAddress);
      setUserphone(userDetails.cachedPhoneNumber);
      updateOtpStatusState({ isOtpSent: true });
      focusFirstTextField();
      updateOtpStatusState({ isVisibleSendOtp: false });
      if (firstTextFieldRef.current) {
        firstTextFieldRef.current.focus();
      }

      setValidatedUserId(userDetails.userId);
      setValidatedUserName(userDetails.userName);
      setValidatedFirstName(userDetails.cachedFirstName);
      setValidatedLastName(userDetails.cachedLastName);
      //setProfileImage(userDetails.profileImage);
      setValidatedProfileImage(userDetails.profileImage ?? null);
      onCheckIsValidUser(userDetails.result);

      return true;
    } else if (userDetails.result === SIGNINRESPONSES.ACCOUNT_LOCKED) {
      // Split out of the group below so the lock reason travels with it, letting the login screen
      // tell an admin lock (contact your administrator) from a failed-attempt lockout (a password
      // reset will unlock you).
      onCheckIsValidUser(userDetails.result, userDetails.isAdminLocked);
    } else if (
      userDetails.result === SIGNINRESPONSES.INVALID_USER ||
      userDetails.result === SIGNINRESPONSES.SESSION_LOCKED ||
      userDetails.result === SIGNINRESPONSES.IP_NOT_WHITELISTED ||
      userDetails.result === SIGNINRESPONSES.ACCOUNT_EXPIRED
    ) {
      onCheckIsValidUser(userDetails.result);
    } else if (userDetails.result === SIGNINRESPONSES.TFA_NOT_CONFIGURED) {
      setUserEmail(userDetails.cachedEmailAddress);
      setUserphone(userDetails.cachedPhoneNumber);
      setValidatedUserId(userDetails.userId);
      setConfirmationDialogMsg(USERSIGNINMESSAGES.AUTH_APP_NOT_CONFIGURED);
      setOpenConfirmation2FA(true);
    } else if (userDetails.result === SIGNINRESPONSES.ALREADY_REGISTRED_WITH_PRIMARY_ACCOUNT) {
      onCheckIsValidUser(SIGNINRESPONSES.ALREADY_REGISTRED_WITH_PRIMARY_ACCOUNT);
    }
  };
  const handleVerifyOtp = async () => {
    if (!otpInput) {
      updateOtpStatusState({ otpErrorMessage: USERSIGNUPMESSAGES.AUTHENTICATION_CODE_REQUIRED });
      focusFirstTextField();
    } else if (otpInput.length < otpFieldLength) {
      updateOtpStatusState({
        otpErrorMessage: USERSIGNUPMESSAGES.AUTHENTICATION_CODE_6_DIGIT_NUMBER,
      });
      focusFirstTextField();
    } else {
      const userData: VerifyTwoFactorUserIdDto = {
        userId: validatedUserId,
        code: otpInput,
      };
      const result = await executeApi<VerifyTwoFactorUserIdDto, TwoFactorResultDto>(
        verify2FACodeByUserId,
        userData,
      );
      if (result.isValid && result.userRole) {
        setToken(result.authToken);
        localStorage.setItem(LOCALSTORAGENAME.ROLENAMES, result.userRole);
        // const res = await executeApi<number, UserProfileDto>(getUserProfileInfo, validatedUserId);
        // if (res) {
        //   setProfileImage(res?.profileImage);
        // }
        onUpdateLoginTimeStamp();
      } else if (result.isValid && !result.userRole) {
        handleCloseDialog();
        onCallParentToRoleStatus(USERSIGNINMESSAGES.ROLE_NOT_ASSIGNED);
      } else {
        updateOtpStatusState({
          otpErrorMessage: USERSIGNUPMESSAGES.AUTHENTICATION_CODE_DOES_NOT_MATCH,
          isVerified: false,
        });
        onIncreaseFailedAttemptsCount();
      }
    }
  };
  const onUpdateLoginTimeStamp = async () => {
    const empAndOtpData: LoginTimeStampDto = {
      userId: validatedUserId,
      sessionValue: localStorage.getItem(USERINFO.SESSIONVALUE),
      rememberForDevice: rememberForDevice,
      deviceId: deviceId,
    };
    const updateResult = await executeApi<LoginTimeStampDto, boolean>(
      updateLoginTimeStamp,
      empAndOtpData,
    );
    if (updateResult) {
      setUserId(validatedUserId);
      setUserName(validatedUserName);
      setFirstName(validatedFistName);
      setLastName(validatedLastName);
      if (validatedProfileImage) {
        setProfileImage(validatedProfileImage);
      } else if (shouldUseEmployeePhotoFallback(validatedProfileImage)) {
        const empProfileImg = await executeApi<number, string>(
          getEmployeeProfileImage,
          validatedUserId,
        );
        if (empProfileImg) setProfileImage(empProfileImg);
      } else {
        // Photo explicitly removed (Bug 79975) - no HR photo fallback, show the initials avatar.
        setProfileImage(null);
      }
      if (rememberForDevice) {
        Cookies.set(COOKIESNAME.USERINPUT, userInput, {
          expires: 365,
          secure: true,
          sameSite: "Strict",
        });
        Cookies.set(COOKIESNAME.USERID, validatedUserId.toString(), {
          expires: 365,
          secure: true,
          sameSite: "Strict",
        });
        Cookies.set(COOKIESNAME.REMEMBERME, rememberForDevice.toString(), {
          expires: 365,
          secure: true,
          sameSite: "Strict",
        });
      } else {
        Object.keys(Cookies.get()).forEach((cookieName) => {
          Cookies.remove(cookieName);
        });
      }
      logAuditTrace(
        {
          userId: validatedUserId,
          objectType: AUDITRACEACTIONS.Login,
          objectId: validatedUserId,
          objectName: "User Id " + validatedUserId,
          action: AUDITRACEACTIONS.Login,
          navigationDateTime: getCurrentESTTime(),
        },
        null,
        true,
      );
      goToHome(validatedUserId);
    }
  };
  const onIncreaseFailedAttemptsCount = async () => {
    const sessionValue = localStorage.getItem(USERINFO.SESSIONVALUE);
    const empAndOtpData: IncreaseFailedAttemptsCountDto = {
      userId: validatedUserId,
      sessionValue: sessionValue,
    };
    const updateResult = await executeApi<IncreaseFailedAttemptsCountDto, string>(
      increaseFailedAttemptsCount,
      empAndOtpData,
    );
    if (updateResult === SIGNINRESPONSES.ACCOUNT_LOCKED) {
      setIsSessionLocked(false);
      setIsAccountLocked(true);
      onCheckIsValidUser(SIGNINRESPONSES.ACCOUNT_LOCKED);
    } else if (updateResult === SIGNINRESPONSES.SESSION_LOCKED) {
      setIsSessionLocked(true);
      setIsAccountLocked(false);
      onCheckIsValidUser(SIGNINRESPONSES.SESSION_LOCKED);
      onIsSessionLocked();
    }
  };
  const focusFirstTextField = () => {
    if (!otpRef.current?.isComplete()) {
      otpRef.current?.focusFirstEmpty();
      return;
    }
  };
  const handleCloseDialog = () => {
    setOpenOTPDialog(false);
  };
  const goToHome = async (userId: number) => {
    if (userId && userId > 0) {
      // A page the user was sent here FROM wins over the landing page - see the same guard in
      // SignIn.tsx. Read only in the no-permissions branch below, it never fired for a real user,
      // so an emailed deep link was discarded at login (Epic 79824 AC1).
      const requestedPath = searchParams.get("redirect") || location.state?.from?.pathname;
      if (requestedPath) {
        navigate(requestedPath, { replace: true });
        return;
      }

      const userModuleRolePermissions = await executeApi<number, UserModuleRolePermission[]>(
        getUserModuleRolePermissions,
        userId,
      );
      if (userModuleRolePermissions?.length > 0) {
        const homePage = userModuleRolePermissions.find((x) =>
          x.pageUrl?.includes(GLOBALSETTINGS.DEFAULT_PAGE),
        );
        if (homePage) {
          navigate(homePage.pageUrl, { replace: true });
        } else {
          const firstUrl = userModuleRolePermissions[0].pageUrl;
          navigate(firstUrl, { replace: true });
        }
      } else {
        const redirectPath =
          searchParams.get("redirect") ||
          location.state?.from?.pathname ||
          GLOBALSETTINGS.DEFAULT_PAGE;

        navigate(redirectPath, { replace: true });
      }
    }
  };
  const handleConfirmationClose2FA = () => {
    setOpenConfirmation2FA(false);
  };
  const handleConfirmation2FAYesClicked = () => {
    setOpenConfirmation2FA(false);
    if (setUpTwoFactorAuthRef.current) {
      setUpTwoFactorAuthRef.current.childComponetCall();
    }
  };
  const handleIsSessionLocked = () => {
    setIsSessionLocked(true);
  };
  const handleSet2FA = () => {
    setConfirmationDialogMsg(USERSIGNINMESSAGES.AUTH_APP_SETUP);
    setOpenConfirmation2FA(true);
  };

  const handleCustomOtpChange = (otp) => {
    setOtpInput(otp);
  };
  return (
    <>
      <BackdropProgressBar loading={loading} />
      <Box textAlign="center">
        <Button
          type="button"
          variant="contained"
          id="btnLoginAuthApp"
          onClick={headerText === "And" ? handleVerifyPasswordAndSendOtp : handleSendOtp}
          fullWidth
        >
          {headerText === "And"
            ? "Send OTP and Proceed with Login"
            : "Login using Authenticator App"}
        </Button>
        {otpStatus.isVerified && (
          <>
            <p>{USERSIGNUPMESSAGES.OTP_VERIFIED_MESSAGE}</p>
            <p>
              <VerifiedIcon className="verified-icon" />
            </p>
          </>
        )}
      </Box>
      <Dialog
        onClose={handleCloseDialog}
        aria-labelledby="customized-dialog-title"
        open={openOTPDialog}
        maxWidth="xs"
        PaperProps={{
          style: {
            width: "480px",
          },
        }}
      >
        <DialogTitle
          id="customized-dialog-title"
          sx={{ fontWeight: "bold", textAlign: "center" }}
          className="dialogtitle-font-size"
        >
          OTP Verification for Authenticator App
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={handleCloseDialog}
          sx={{
            position: "absolute",
            right: 12,
            top: 12,
            color: "#fff",
          }}
        >
          <CloseIcon />
        </IconButton>
        {otpStatus.isOtpSent && (
          <>
            <BackdropProgressBar loading={loading} />
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <img
                src="/two-step-verification.gif"
                alt="description"
                style={{ width: "100px", height: "100px" }}
              />
            </div>
            {isSessionLocked && (
              <Alert severity="error" sx={{ marginBottom: "10px" }} className="alert-alert">
                {USERSIGNINMESSAGES.SESSION_LOCKED_MESSAGE.replace(
                  "{Session_value}",
                  localStorage.getItem(USERINFO.SESSIONVALUE),
                )}
              </Alert>
            )}
            {isAccountLocked && (
              <Alert severity="error" sx={{ marginBottom: "10px" }} className="alert-success-popup">
                {USERSIGNINMESSAGES.ACCOUNT_LOCKED_MESSAGE}
              </Alert>
            )}
            <Typography
              sx={{
                textAlign: "center",
                fontSize: "14px",
                marginTop: "7px",
              }}
              id="msgTfaRequired"
            >
              {USERSIGNINMESSAGES.TFA_REQUIRED_MESSAGE}
            </Typography>
            <div className="div-align">
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  width: "100%",
                  px: 2,
                }}
              >
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  gap={2}
                  sx={{
                    flexWrap: "wrap",
                    width: "100%",
                    maxWidth: {
                      xs: "100%",
                      sm: `${otpFieldLength * 63}px`,
                    },
                  }}
                >
                  <OtpInput length={otpFieldLength} onChange={handleCustomOtpChange} ref={otpRef} />
                </Box>
              </Box>
              <p className="required-msg">{otpStatus.otpErrorMessage}</p>

              <Button
                type="button"
                variant="contained"
                onClick={handleVerifyOtp}
                disabled={isSessionLocked || isAccountLocked}
                className="btn-margin"
              >
                Verify And Login
              </Button>
            </div>
            {!isSessionLocked && (
              <Link
                component="button"
                type="button"
                onClick={handleSet2FA}
                variant="body2"
                disabled={isSessionLocked}
                sx={{ alignSelf: "center", marginTop: "5px" }}
              >
                {USERSIGNINMESSAGES.LOST_ACCESS_AUTH_APP}
              </Link>
            )}
            {authModeName === SIGNUPMODE.SIGNUP_WITH_PASSWORD_OR_OTP && (
              <Typography sx={{ textAlign: "center", fontSize: "14px", paddingTop: "10px" }}>
                Login using{" "}
                <Link variant="body2" sx={{ alignSelf: "center" }} onClick={handleCloseDialog}>
                  Password
                </Link>
              </Typography>
            )}
          </>
        )}
        <DialogActions sx={{ justifyContent: "center", pb: 2 }}></DialogActions>
      </Dialog>
      <Dialog
        open={openConfirmation2FA}
        onClose={handleConfirmationClose2FA}
        aria-labelledby="responsive-dialog-title"
      >
        <DialogTitle
          id="responsive-dialog-title"
          className="dialogtitle-font-size dialogtitle-padding-right"
        >
          {confirmationDialogMsg}
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={handleConfirmationClose2FA}
          sx={() => ({
            position: "absolute",
            right: 8,
            top: 8,
            color: "#fff",
          })}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent dividers>
          <Typography gutterBottom>Do you want to set up App?</Typography>
        </DialogContent>
        <DialogActions>
          <Button autoFocus onClick={handleConfirmationClose2FA} color="light" variant="contained">
            No
          </Button>
          <Button onClick={handleConfirmation2FAYesClicked} autoFocus variant="contained">
            Yes
          </Button>
        </DialogActions>
      </Dialog>

      <SetUpTwoFactorAuth
        userId={validatedUserId}
        email={userEmail}
        phone={userPhone}
        userName={validatedUserName}
        otpSendTo={otpSendTo}
        onIsSessionLocked={handleIsSessionLocked}
        ref={setUpTwoFactorAuthRef}
      ></SetUpTwoFactorAuth>
    </>
  );
};
export default SignInWithOTP;
